import {reverse} from "strings";
import {v} from "../react/cst2";

export const findMinE = <E,V>(col: E[], getValue: (e: E) => V, quickStop: V = undefined) => {
    let minVal : V = undefined;
    let minEle : E = undefined;

    for (const e of col) {
        const value = getValue(e);
        if (value == null) {
            continue;
        }

        if (quickStop !== undefined && quickStop===value) {
            return e;
        }
        if (minVal === undefined || minVal > value) {
            const v = value;
            if (v != null) {
                minVal = v;
                minEle = e;
            }
        }
    }
    return minEle;
};

export const findMaxE = <E,V>(col: E[], getValue: (e: E) => V, quickStop: (V, E)=>boolean = undefined) => {
    let maxVal : V = undefined;
    let maxEle : E = undefined;

    for (const e of col) {
        const value = getValue(e);
        if (quickStop !== undefined && quickStop(value, e)) {
            return e;
        }

        if (maxVal === undefined || maxVal < value) {
            const v = value;
            if (v != null) {
                maxVal = v;
                maxEle = e;
            }
        }
    }
    return maxEle;
};

export const last = (col) => {
    return col?.[col.length - 1];
};

export const findMinValue = <E,V>(col: E[], getValue: (e: E) => V = (e)=><V><unknown>e, {gt = compares.gt}={}) => {
    if (col==null) {
        return null;
    }

    let minVal : V = undefined;

    for (const e of col) {
        if (e == null) {
            continue;
        }
        const v = getValue(e);
        if (minVal === undefined || gt(minVal, v)) {
            if (v != null) {
                minVal = v;
            }
        }
    }
    return minVal === undefined ? null : minVal;
};

export const compares = {
    le: <V> (v1: V, v2: V) => v1 <= v2,
    lt: <V> (v1: V, v2: V) => v1 <  v2,
    ge: <V> (v1: V, v2: V) => v1 >= v2,
    gt: <V> (v1: V, v2: V) => v1 >  v2,
};

export const findMaxValue = <E,V>(col: E[], getValue: (e: E) => V = (e) => <V><unknown>e, {lt = compares.lt}={}) => {
    if (col==null) {
        return null;
    }
    let maxVal: V = undefined;

    for (const e of col) {
        if (e == null) {
            continue;
        }
        const value = getValue(e);
        if (maxVal === undefined || lt(maxVal, value)) {
            if (value != null) {
                maxVal = value;
            }
        }
    }
    return maxVal === undefined ? null : maxVal;
};

export const findMinIndex = <E,V>(col: E[], getValue: (e: E) => V) => {
    let minVal: V = undefined;
    let minIndex: number = undefined;

    for (let i = 0; i < col.length; i++) {
        const e = col[i];

        if (minVal === undefined || minVal > getValue(e)) {
            const v = getValue(e);
            if (v != null) {
                minVal = v;
                minIndex = i;
            }
        }
    }
    return minIndex;
};

export const addRemove = <E,>(col: E[]) => (element : E) => {
    col.push(element);

    return () => remove1Mutate(col, element);
};

export function removeAll<E>(col: E[], targets: E[]) {
    return col.filter((e) => !~targets.indexOf(e));
}

export function remove1Mutate<E>(col: E[], targetElem: E) {
    if (col == null) {
        return;
    }

    let i = col.indexOf(targetElem);
    if (i === -1) {
        return;
    }
    col.splice(i, 1);
}

export function without<E>(targetElem: E, col: E[]) {
    if (col == null) {
        return;
    }
    return col.filter((e) => e!==targetElem);
}

export function unique<T>(col:T[], by:(v:T) => any = v => v): T[] {
    if (col == null) {
        return null;
    }
    const ret  = [];
    const keys = [];
    for (const e of col) {
        const key = by(e);
        if (!~keys.indexOf(key)) {
            ret.push(e);
            keys.push(key);
        }
    }
    return ret;
}

export function indexAttr(col : {}[], keyAttr: string, valueAttr: string | null) {
    if (col == null) {
        return {};
    }
    let map = {};
    for (let i = 0; i < col.length; i++) {
        const e = col[i];
        if (map.hasOwnProperty(e[keyAttr])) {
            throw `Property existed [${e[keyAttr]}]`;
        }
        map[e[keyAttr]] = valueAttr ? e[valueAttr] : e;
    }
    return map;
}

export function index<E,V>(col: E[], getKey: (e: E) => string, getValue: (e: E) => V=undefined) {
    if (col == null) {
        return {};
    }
    let map = {};
    for (let i = 0; i < col.length; i++) {
        const e = col[i];
        if (map.hasOwnProperty(getKey(e))) {
            throw `Property existed [${getKey(e)}]`;
        }
        map[getKey(e)] = getValue ? getValue(e) : e;
    }
    return map;
}

export function indexAttrMulti(col: {}[], keyAttr: string, valueAttr: string | null) {
    if (col == null) {
        return {};
    }
    let map = {};
    for (let i = 0; i < col.length; i++) {
        const e = col[i];
        let list = map[e[keyAttr]];
        if (list == null) {
            list = [];
            map[e[keyAttr]] = list;
        }
        list.push(valueAttr ? e[valueAttr] : e);
    }
    return map;
}

export function sort<T>(col: T[], byFn: (e: T) => any = v=>v, reverse: boolean = false) {
    if (col == null) {
        return null;
    }
    let clone = col.slice(0);
    clone.sort((e1, e2) => byFn(e1) > byFn(e2) ? 1 : -1);
    return reverse ? clone.reverse() : clone;
}

export function sortMulti(col: any[], byFns: ((e) => any)[]) {
    if (col == null) {
        return null;
    }
    let clone = col.slice(0);
    clone.sort((e1, e2) => compareSortValues(
        byFns.map((by) => by(e1)),
        byFns.map((by) => by(e2)),
    ));
    return clone;
}

export const compareSortValues = (arr1, arr2) => {
    for (let i = 0;i < Math.min(arr1.length, arr2.length); i++) {
        if (arr1[i] > arr2[i]) {
            return 1;
        }
        if (arr1[i] < arr2[i]) {
            return -1;
        }
    }
    return 0;
};

export function reverse(col) {
    if (col == null) return null;

    let clone = col.slice(0);
    clone.reverse();
    return clone;
}

export const createArray = (length: number) => {
    if (isNaN(length)) {
        throw "Length is not a number: " + length;
    }
    return new Array(length).fill(0).map((_, i) => i);
};

export function addToSet<E>(set: E[], items: E[]) {
    set = set.slice(0);
    for (const item of items) {
        if (!~set.indexOf(item)) {
            set.push(item);
        }
    }
    return set;
}

export function addToListMap<A>(listMap: {[key: string] : A[]}, key: string, item : A) {
    let list = listMap[key];
    if (list == null) {
        list = [];
        listMap[key] = list;
    }
    list.push(item);
}

export function concatToListMap<A>(listMap: {[key: string] : A[]}, key: string, items: A[]) {
    listMap[key] = [...listMap[key]||[], ...items];
}

export function replace<E>(col: E[], targets: E[], withs: E[]) {
    return col.map((e) => {
        const indexOf = targets.indexOf(e);
        if (indexOf > -1) {
            return withs[indexOf];
        } else {
            return e;
        }
    });
}

export function replace1<E>(col: E[], target: E, with1: E) {
    return col.map((e) => e === target ? with1 : e);
}

export function split<E,V>(col: E[], by: (e: E) => V): E[][] {
    if (col == null) {
        return [];
    }
    let trues: E[] = [];
    let falses: E[] = [];
    for (const e of col) {
        (by(e) ? trues : falses).push(e);
    }
    return [trues, falses];
}

export function flatten1<E>(col: Array<E | E[]>) {
    if (col == null) {
        return [];
    }
    let ret: E[] = [];
    for (const arr of col) {
        ret = ret.concat(arr);
    }
    return ret;
}

export const removeIndex = <E>(index: number, col: E[]) => {
    return col.filter((e, i) => i !== index);
};

export const toMap = <E>(arr : E[], indexToKey: (i: number) => string) => {
    const ret : {[key: string]: E} = {};
    for (let i = 0; i < arr.length; i++) {
        ret[indexToKey(i)] = arr[i];
    }
    return ret;
};

export function sum(arr: any[], getValue: (e: any) => number = (e) => <number><unknown>e) {
    if (arr==null) {
        return 0;
    }
    return arr.reduce((t, e) => t+(getValue(e)||0), 0);
}

export const switchAmong = (arr: (() => []) | []): (() => any) => {
    const getArr = typeof arr === "function" ? arr : () => arr;

    let index = 0;

    return () => {
        index++;
        const a = getArr();
        if (index >= a.length) {
            index = 0;
        }
        return a[index];
    };
};


export const divide = <E>(col: E[], weights: number[]) => {

    let ret: E[][] = [];
    let extracted = 0;
    weights.forEach((weight, di) => {
        const end: number = di === weights.length - 1 ? col.length :
            extracted + Math.floor(weight*col.length)
        ;
        ret.push(col.slice(extracted, end));
        extracted = end;
    });

    return ret;
};


export const swap = <V>({get, set}: {get: (k: string) => V, set: (v: V, k: string) => void}, newKey: string, oldKey: string) => {
    const oldValue = get(oldKey);
    const newValue = get(newKey);

    set(oldValue, newKey);
    set(newValue, oldKey);
};


export const crossMap = <E>(cols: E[][], fn: (arr: E[]) => any): (E[] | E)[] => {
    if (cols == null || cols.length === 0) {
        return [];
    }
    if (cols.length === 1) {
        return cols[0].map((e) => fn([e]));
    }
    const [firstCol, ...lastCols] = cols;

    let ret = [];
    for (const e of firstCol) {
        ret = [...ret, ...crossMap(lastCols, (path) => fn([e, ...path]))];
    }
    return ret;
};



export const toGroups = <E>(col: E[], groupSize: number) => {
    let ret: E[][] = [];
    for (;col.length > 0;) {
        ret.push(col.slice(0, groupSize));
        col = col.slice(groupSize);
    }
    return ret;
};

export const toColumns = (col, colCount: number) : any[] => {
    return toGroups(col, Math.ceil(col.length / colCount));
};

/**
 * Key is primitive
 */
export const toGroupsByKey = (col, getKey = v=>v) => {
    if (!col) {
        return null;
    }
    let ret = {};
    for (const e of col) {
        const key = getKey(e);
        let list = ret[key];
        if (list == null) {
            list = [];
            ret[key] = list;
        }
        list.push(e);
    }
    return ret;
};


export const isEmpty = <E>(col: E[]) => {
    return col == null || col.length === 0;
};

export const isNotEmpty = (col) => {
    return !isEmpty(col);
};

export const arrEquals = (a1, a2, compare=(v1,v2) => v1 === v2) => {

    if (a1 == null) {
        return a2 == null;
    }
    if (a2 == null) {
        return false;
    }
    if (a1.length !== a2.length) {
        return false;
    }

    for (let i = 0; i < a1.length; i++) {
        if (!compare(a1[i], a2[i])) {
            return false;
        }
    }

    return true;
};

export const replaceIndex = <T>(index: number, col: T[], newE: T) => {
    // return col.map((e, i) => i !== index ? e : newE);
    return [
        ...col.slice(0, index),
        newE,
        ...col.slice(index + 1),
    ];
};

/**
 * @deprecated
 */
export const changeIndex = replaceIndex;

export function replaceFind(col, newE, find) {
    if (col == null) {
        return null;
    }
    const index = col.findIndex(find);
    if (index === -1) {
        return col;
    }
    return replaceIndex(index, col, newE);
}

export function replaceFind_f(col, getNewE, find) {
    if (col == null) {
        return null;
    }
    const index = col.findIndex(find);
    if (index === -1) {
        return col;
    }
    return replaceIndex(index, col, getNewE(col[index]));
}

export const merge = (col, getKey, merge) => {
    col = col.slice(0);

    let ret = [];
    for (let i = 0; i < col.length; i++) {
        let e = col[i];
        const key = getKey(e);

        for (let j = i+1; j < col.length; j++) {
            const e2 = col[j];
            if (getKey(e2) === key) {
                e = merge(e, e2);
                col.splice(j, 1);
                j--;
            }
        }
        ret.push(e);
    }
    return ret;
};


export const joinArr = (arr, delimiter) => {
    let ret = [];
    for (let i = 0; i < arr.length; i++) {
        const e = arr[i];
        if (i > 0) {
            ret.push(delimiter(i, arr.length));
        }
        ret.push(e);
    }
    return ret;
};

export const toggleE = <Item>(e:Item, col:Item[]) => {
    const i = col.indexOf(e);
    if (i === -1) {
        return [...col, e];
    } else {
        return col.filter((_, i1) => i1 !== i);
    }
};

export const limitLength = (arr, {length, cutHead=false}) => {
    if (!arr || arr.length <=length) {
        return arr;
    }
    return cutHead ? (
        arr.slice(arr.length - length)
    ) : (
        arr.slice(0, length)
    );
};


export const findLast = (col, find) => {
    for (let i = col.length - 1; i > -1; i--) {
        const e = col[i];
        if (find(e)) {
            return e;
        }
    }
};

export const moveItem = <T>(list: T[], fromIndex: number, toIndex: number) => {
    const newList = list.slice();
    const [moved] = newList.splice(fromIndex, 1);
    newList.splice(toIndex, 0, moved);
    return newList;
};

/**
 * Keep from A only items not in B
 */
export const diffItems = <T>(
    listA: T[],
    listB: T[],
    equals: (itemA: T, itemB: T) => boolean = (itemA, itemB) => itemA === itemB,
): T[] => (
    listA.filter(itemA => !listB.some(itemB => equals(itemA, itemB)))
);


export const findDuplicateds2 = (col, {by=v=>v, compare=(a,b)=>a===b}) => {
    const ret = [];
    for (let i = 0; i < col.length; i++) {
        const e = col[i];
        if (ret.some((d) => d.dups.includes(e))) {
            continue;
        }
        const by1 = by(e);
        let dups;
        for (let j = i+1; j < col.length; j++) {
            const e2 = col[j];
            const by2 = by(e2);
            if (compare(by1, by2)) {
                if (!dups) {
                    dups = [e];
                }
                dups.push(e2);
            }
        }
        if (dups) {
            ret.push({by: by1, dups});
        }
    }
    return ret;
};

